/**
 * @module agent/adapters/services/RevealPollCoordinator
 *
 * Coordinates the bulk-reveal polling lifecycle (TD-2293, shipped TD-2281
 * contract). Mirrors SearchPollCoordinator in a distinct REVEAL_* namespace:
 *
 *   1. The agent harness emits [REVEAL_STARTED:<jobId>] after a confirmed bulk
 *      reveal → start polling.
 *   2. Every ~20s send a hidden `CHECK_REVEAL_RESULTS:<jobId>`; a `[REVEAL_BUSY]`
 *      response means the job is still pending → reschedule. (The adapter funnel
 *      hides `[REVEAL_BUSY]` turns; this coordinator only owns the schedule.)
 *   3. A `[REVEAL_RESULT]` turn (values / cancel / error / no-match) is terminal
 *      → stop polling. TD-2442: unrelated tag-free turns are IGNORED so an
 *      interleaved search/user turn can't prematurely stop the reveal loop.
 *
 * The RevealProgressCard additionally polls the job status endpoint directly
 * (2s, live X/Y) and fires one manual CHECK when it sees completion — the
 * adapter routes that through notifyManualPoll() so the 20s timer resets and
 * the agent never receives two near-simultaneous checks producing duplicate
 * render turns.
 *
 * Reveal polling deliberately does NOT drive the agent 'searching' state: the
 * input stays usable while a bulk reveal runs in the background.
 */

import { parseEnrichmentTags, buildRevealPollMessage } from './EnrichmentTagService';
import { REVEAL_POLL_INTERVAL_MS, REVEAL_MARKER } from '../../../../utils/constants/agentEnrichment';

/** Max scheduled polls before giving up (15 × 20s = 5 min, mirrors search). */
export const REVEAL_MAX_POLL_ATTEMPTS = 15;

class RevealPollCoordinator {
  /**
   * @param {Object} callbacks
   * @param {Function} callbacks.sendHiddenMessage - Async (sessionId, content) poll sender
   * @throws {TypeError} If callbacks are missing/invalid
   */
  constructor(callbacks) {
    if (!callbacks || typeof callbacks !== 'object') {
      throw new TypeError('RevealPollCoordinator: callbacks must be an object');
    }
    if (callbacks.sendHiddenMessage && typeof callbacks.sendHiddenMessage !== 'function') {
      throw new TypeError('RevealPollCoordinator: callbacks.sendHiddenMessage must be a function');
    }

    this._callbacks = callbacks;
    this._state = { active: false, jobId: null, sessionId: null, pollAttempts: 0 };
    this._pollTimeout = null;
    this._isDestroyed = false;
  }

  get isDestroyed() {
    return this._isDestroyed;
  }

  /** True while a bulk-reveal poll loop runs for this session. */
  isPollingForSession(sessionId) {
    if (this._isDestroyed) return false;
    return this._state.active && this._state.sessionId === sessionId;
  }

  /**
   * Inspect a response's reveal tags and drive the poll lifecycle.
   * Called from the adapter's tag funnel for every finalized response —
   * AFTER SearchPollCoordinator.handleSearchTags (which owns visibility).
   *
   * @param {string} content - Raw response content
   * @param {string} sessionId - Session the response belongs to
   */
  handleRevealTags(content, sessionId) {
    if (this._isDestroyed || !content || typeof content !== 'string') return;

    const enrichment = parseEnrichmentTags(content);

    // 1. [REVEAL_STARTED:<jobId>] — begin polling for this job.
    if (enrichment?.revealJob) {
      if (import.meta.env.DEV) {
        console.log('🔓 RevealPollCoordinator: bulk reveal started:', enrichment.revealJob);
      }
      this._startPolling(enrichment.revealJob, sessionId);
      return;
    }

    // Everything below only matters while a poll loop is active for THIS
    // session (a marker from another session must not touch it).
    if (!this.isPollingForSession(sessionId)) return;

    // 2. [REVEAL_BUSY] — job still pending; keep the loop alive.
    if (content.includes(REVEAL_MARKER.BUSY)) {
      if (import.meta.env.DEV) console.log('⏳ RevealPollCoordinator: job still pending');
      this._scheduleNextPoll();
      return;
    }

    // 3. [REVEAL_RESULT] — a terminal reveal turn (values / cancel / error /
    // no-match) presented the outcome; stop polling.
    if (content.includes(REVEAL_MARKER.RESULT)) {
      if (import.meta.env.DEV) console.log('🎉 RevealPollCoordinator: reveal result arrived, stopping');
      this._stop();
    }

    // 4. Any other turn is NOT a reveal-loop response — e.g. a normal user turn
    // or a search result interleaved while the input stays usable (TD-2442).
    // Ignore it and keep polling; the loop keys only on its own markers.
  }

  /**
   * The UI sent its own CHECK_REVEAL_RESULTS (fast-finish path from the
   * progress card's direct job polling). Reset the pending timer so the
   * scheduled poll doesn't fire a near-duplicate check; the response to the
   * manual check drives the lifecycle from here ([BUSY] → reschedule,
   * render turn → stop).
   *
   * @param {string} sessionId - Session the manual poll was sent to
   */
  notifyManualPoll(sessionId) {
    if (!this.isPollingForSession(sessionId)) return;
    if (import.meta.env.DEV) console.log('🔓 RevealPollCoordinator: manual poll — timer reset');
    this._clearTimeout();
  }

  /** Stop polling when the user stops the session's in-flight work (TD-2347). */
  stopForSession(sessionId) {
    if (this._isDestroyed || !this.isPollingForSession(sessionId)) return false;
    this._stop();
    return true;
  }

  _startPolling(jobId, sessionId) {
    this._clearTimeout();
    this._state = { active: true, jobId, sessionId, pollAttempts: 0 };
    this._scheduleNextPoll();
  }

  _scheduleNextPoll() {
    if (!this._state.active) return;

    this._state.pollAttempts += 1;
    if (this._state.pollAttempts > REVEAL_MAX_POLL_ATTEMPTS) {
      // Give up silently — the job may still finish server-side; the user can
      // ask the agent for the results (a fresh CHECK) at any time.
      if (import.meta.env.DEV) {
        console.warn('⏰ RevealPollCoordinator: max poll attempts reached, stopping');
      }
      this._stop();
      return;
    }

    this._clearTimeout();
    const { jobId, sessionId } = this._state;
    this._pollTimeout = setTimeout(async () => {
      if (!this._state.active) return;
      try {
        await this._callbacks.sendHiddenMessage?.(sessionId, buildRevealPollMessage(jobId));
      } catch (error) {
        console.error('❌ RevealPollCoordinator: poll error:', error);
        // Schedule another attempt; the attempt counter bounds total retries.
        this._scheduleNextPoll();
      }
    }, REVEAL_POLL_INTERVAL_MS);
  }

  _clearTimeout() {
    if (this._pollTimeout) {
      clearTimeout(this._pollTimeout);
      this._pollTimeout = null;
    }
  }

  _stop() {
    this._clearTimeout();
    this._state = { active: false, jobId: null, sessionId: null, pollAttempts: 0 };
  }

  /** Clean up resources. Safe to call multiple times. */
  destroy() {
    if (this._isDestroyed) return;
    this._stop();
    this._callbacks = null;
    this._isDestroyed = true;
  }
}

export default RevealPollCoordinator;
